// This file is automatically compiled by Webpack, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/javascript and only use these pack files to reference
// that code so it'll be compiled.

import Rails from "@rails/ujs"
import Turbolinks from "turbolinks"
import * as ActiveStorage from "@rails/activestorage"
import "channels"

import "bootstrap"
import { Tooltip, Popover } from "bootstrap"
import '../stylesheets/application'


Rails.start()
Turbolinks.start()
ActiveStorage.start()

function getColorLuminance(hex) {
    if (!hex) return 128;
    const h = hex.replace('#', '');
    if (h.length < 6) return 128;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

function pickTeamColor(primary, secondary, isDark) {
    if (!secondary) return primary;
    const lumP = getColorLuminance(primary);
    if (isDark && lumP < 80) {
        const lumS = getColorLuminance(secondary);
        return lumS > lumP ? secondary : primary;
    }
    if (!isDark && lumP > 200) {
        const lumS = getColorLuminance(secondary);
        return lumS < lumP ? secondary : primary;
    }
    return primary;
}

function applyTeamColors() {
    const isDark = document.documentElement.getAttribute('data-bs-theme') === 'dark';
    document.querySelectorAll('.progress[data-primary-color]').forEach(bar => {
        const color = pickTeamColor(bar.dataset.primaryColor, bar.dataset.secondaryColor, isDark);
        bar.querySelectorAll('.progress-bar').forEach(segment => {
            segment.style.backgroundColor = color;
        });
    });
}

function getStoredTheme() {
    const v = localStorage.getItem('theme');
    return (v === 'light' || v === 'dark') ? v : 'auto';
}

function systemTheme() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolvedTheme() {
    const s = getStoredTheme();
    return s === 'auto' ? systemTheme() : s;
}

function updateThemeIcons() {
    const stored = getStoredTheme();
    const sun = document.getElementById('dark-mode-sun');
    const moon = document.getElementById('dark-mode-moon');
    const monitor = document.getElementById('dark-mode-monitor');
    if (!sun || !moon || !monitor) return;
    sun.style.display = stored === 'light' ? 'block' : 'none';
    moon.style.display = stored === 'dark' ? 'block' : 'none';
    monitor.style.display = stored === 'auto' ? 'block' : 'none';
}

function applyTheme() {
    document.documentElement.setAttribute('data-bs-theme', resolvedTheme());
    updateThemeIcons();
    applyTeamColors();
}

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (getStoredTheme() === 'auto') applyTheme();
});

document.addEventListener("turbolinks:load", () => {
    const toggle = document.getElementById('dark-mode-toggle');
    if (toggle) {
        updateThemeIcons();
        toggle.addEventListener('click', () => {
            const next = { auto: 'light', light: 'dark', dark: 'auto' }[getStoredTheme()];
            if (next === 'auto') localStorage.removeItem('theme');
            else localStorage.setItem('theme', next);
            applyTheme();
        });
    }

    applyTeamColors();

    // Both of these are from the Bootstrap 5 docs
    var tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'))
    var tooltipList = tooltipTriggerList.map(function(tooltipTriggerEl) {
        return new Tooltip(tooltipTriggerEl)
    })

    var popoverTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="popover"]'))
    var popoverList = popoverTriggerList.map(function(popoverTriggerEl) {
        return new Popover(popoverTriggerEl)
    })

    // Simulation select functionality
    document.querySelectorAll('.simulation-select').forEach(select => {
        // Prevent click from triggering sortable header
        select.addEventListener('click', function(e) {
            e.stopPropagation()
        })

        select.addEventListener('change', function(e) {
            e.stopPropagation()
            const matchupId = this.dataset.matchupId
            const round = this.dataset.round
            const outcome = this.value
            const url = new URL(window.location)
            const params = new URLSearchParams(url.search)

            // Get existing sim params
            const existingSims = params.getAll('sim[]')

            // Remove any existing simulation for this matchup
            const prefix = `${matchupId}:`
            const filteredSims = existingSims.filter(s => !s.startsWith(prefix))

            // Add new simulation if an outcome was selected
            if (outcome) {
                filteredSims.push(`${matchupId}:${outcome}`)
            }

            // Build new URL with round as path param
            const pathParts = url.pathname.split('/').filter(p => p)
            // Path is /:sport/:year or /:sport/:year/:round
            const sport = pathParts[0]
            const year = pathParts[1]
            const newPath = `/${sport}/${year}/${round}`

            // Update URL params
            params.delete('sim[]')
            params.delete('round')
            filteredSims.forEach(sim => params.append('sim[]', sim))

            // Build final URL (decode to avoid ugly URL encoding)
            const queryString = decodeURIComponent(params.toString())
            const newUrl = queryString ? `${newPath}?${queryString}` : newPath
            Turbolinks.visit(newUrl)
        })
    })

    // Sortable round table functionality
    document.querySelectorAll('.sortable-header').forEach(header => {
        header.addEventListener('click', function() {
            const table = this.closest('table')
            const tbody = table.querySelector('tbody')
            const rows = Array.from(tbody.querySelectorAll('tr'))
            const columnIndex = this.dataset.columnIndex
            const isTotal = this.dataset.sortType === 'total'
            const isOverallTotal = this.dataset.sortType === 'overall-total'
            const currentSort = this.dataset.sortState || 'none'

            let newSort
            if (isTotal || isOverallTotal) {
                newSort = 'total'
            } else if (columnIndex) {
                // Cycle through: none -> scoring_index -> max_points_desc -> max_points_asc -> scoring_index
                if (currentSort === 'none' || currentSort === 'max_points_asc') {
                    newSort = 'scoring_index'
                } else if (currentSort === 'scoring_index') {
                    newSort = 'max_points_desc'
                } else {
                    newSort = 'max_points_asc'
                }
            }

            // Remove sort indicators from all headers
            table.querySelectorAll('.sortable-header').forEach(h => {
                h.classList.remove('sorted-asc', 'sorted-desc', 'sorted-both')
                h.dataset.sortState = 'none'
            })

            // Add sort indicator to current header
            if (newSort === 'scoring_index') {
                this.classList.add('sorted-both')
            } else if (newSort === 'max_points_asc' || newSort === 'total') {
                this.classList.add('sorted-asc')
            } else if (newSort === 'max_points_desc') {
                this.classList.add('sorted-desc')
            }
            this.dataset.sortState = newSort

            // Sort rows
            rows.sort((a, b) => {
                let aVal, bVal

                if (isOverallTotal) {
                    // For overall total column, sort by max_total with min_total as tiebreaker
                    const aCell = a.querySelector('.overall-total-cell')
                    const bCell = b.querySelector('.overall-total-cell')
                    const aMax = aCell ? parseFloat(aCell.dataset.maxTotal || '0') : 0
                    const bMax = bCell ? parseFloat(bCell.dataset.maxTotal || '0') : 0
                    const aMin = aCell ? parseFloat(aCell.dataset.minTotal || '0') : 0
                    const bMin = bCell ? parseFloat(bCell.dataset.minTotal || '0') : 0

                    const maxDiff = bMax - aMax // Descending by max
                    if (maxDiff !== 0) return maxDiff

                    return bMin - aMin // Tiebreaker: descending by min
                } else if (isTotal) {
                    // For round total column, use original rank
                    aVal = parseInt(a.cells[0].textContent)
                    bVal = parseInt(b.cells[0].textContent)
                    return aVal - bVal // Always ascending for default rank
                } else if (newSort === 'scoring_index') {
                    // Sort by scoring_index ascending
                    const aCell = a.querySelector(`[data-column-index="${columnIndex}"]`)
                    const bCell = b.querySelector(`[data-column-index="${columnIndex}"]`)
                    aVal = aCell ? parseFloat(aCell.dataset.scoringIndex || '999') : 999
                    bVal = bCell ? parseFloat(bCell.dataset.scoringIndex || '999') : 999
                    return aVal - bVal
                } else {
                    // For matchup columns, sort by max_points with min_points as tiebreaker
                    const aCell = a.querySelector(`[data-column-index="${columnIndex}"]`)
                    const bCell = b.querySelector(`[data-column-index="${columnIndex}"]`)
                    const aMax = aCell ? parseFloat(aCell.dataset.sortValue || '0') : 0
                    const bMax = bCell ? parseFloat(bCell.dataset.sortValue || '0') : 0
                    const aMin = aCell ? parseFloat(aCell.dataset.minPoints || '0') : 0
                    const bMin = bCell ? parseFloat(bCell.dataset.minPoints || '0') : 0

                    const maxDiff = newSort === 'max_points_desc' ? bMax - aMax : aMax - bMax
                    if (maxDiff !== 0) return maxDiff

                    // Tiebreaker: use min_points in same direction
                    return newSort === 'max_points_desc' ? bMin - aMin : aMin - bMin
                }
            })

            // Re-append rows in sorted order
            rows.forEach(row => tbody.appendChild(row))
        })
    })
})
